import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type ReactNode,
} from "react";
import type { ChartType, RangeKey } from "@chartandgo/shared";
import type { Asset } from "@chartandgo/shared";
import {
  basketFromSnapshot,
  duplicateBasket as duplicateBasketModel,
  type BasketSnapshot,
} from "../lib/basketTransfer.js";
import {
  createDefaultState,
  createStockBasket,
  loadWorkspace,
  saveWorkspace,
  workspaceReducer,
  type ChartCard,
  type CardPlacement,
  type GridItem,
  type Theme,
  type WorkspaceState,
} from "./workspace.js";

interface WorkspaceContextValue {
  state: WorkspaceState;
  addCard: (
    asset: Pick<Asset, "symbol" | "name">,
    layout?: CardPlacement,
  ) => void;
  removeCard: (id: string) => void;
  updateCard: (id: string, changes: Partial<Omit<ChartCard, "id">>) => void;
  setLayout: (layout: GridItem[]) => void;
  createBasket: (name: string) => void;
  duplicateBasket: (id: string) => void;
  importBasket: (snapshot: BasketSnapshot) => void;
  selectBasket: (id: string) => void;
  renameBasket: (id: string, name: string) => void;
  removeBasket: (id: string) => void;
  toggleTheme: () => void;
  reset: () => void;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

const DEFAULT_RANGE: RangeKey = "6M";
const DEFAULT_CHART_TYPE: ChartType = "candlestick";

function makeId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `card-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(
    workspaceReducer,
    undefined,
    () =>
      typeof window === "undefined"
        ? createDefaultState()
        : loadWorkspace(window.localStorage),
  );

  useEffect(() => {
    saveWorkspace(window.localStorage, state);
  }, [state]);

  useEffect(() => {
    document.documentElement.dataset.theme = state.theme;
  }, [state.theme]);

  const addCard = useCallback(
    (asset: Pick<Asset, "symbol" | "name">, layout?: CardPlacement) => {
      dispatch({
        type: "addCard",
        card: {
          id: makeId(),
          symbol: asset.symbol.toUpperCase(),
          name: asset.name,
          range: DEFAULT_RANGE,
          chartType: DEFAULT_CHART_TYPE,
          showVolume: true,
        },
        layout,
      });
    },
    [],
  );

  const removeCard = useCallback((id: string) => {
    dispatch({ type: "removeCard", id });
  }, []);

  const updateCard = useCallback(
    (id: string, changes: Partial<Omit<ChartCard, "id">>) => {
      dispatch({ type: "updateCard", id, changes });
    },
    [],
  );

  const setLayout = useCallback((layout: GridItem[]) => {
    dispatch({ type: "setLayout", layout });
  }, []);

  const createBasket = useCallback((name: string) => {
    const id = makeId();
    dispatch({
      type: "createBasket",
      basket: createStockBasket(id, name),
    });
  }, []);

  const duplicateBasket = useCallback(
    (id: string) => {
      const basket = state.baskets.find((candidate) => candidate.id === id);
      if (!basket) return;
      dispatch({
        type: "createBasket",
        basket: duplicateBasketModel(basket, makeId),
      });
    },
    [state.baskets],
  );

  const importBasket = useCallback((snapshot: BasketSnapshot) => {
    dispatch({
      type: "createBasket",
      basket: basketFromSnapshot(snapshot, makeId),
    });
  }, []);

  const selectBasket = useCallback((id: string) => {
    dispatch({ type: "selectBasket", id });
  }, []);

  const renameBasket = useCallback((id: string, name: string) => {
    dispatch({ type: "renameBasket", id, name });
  }, []);

  const removeBasket = useCallback((id: string) => {
    dispatch({ type: "removeBasket", id });
  }, []);

  const toggleTheme = useCallback(() => {
    dispatch({
      type: "setTheme",
      theme: (state.theme === "dark" ? "light" : "dark") as Theme,
    });
  }, [state.theme]);

  const reset = useCallback(() => dispatch({ type: "reset" }), []);

  const value = useMemo(
    () => ({
      state,
      addCard,
      removeCard,
      updateCard,
      setLayout,
      createBasket,
      duplicateBasket,
      importBasket,
      selectBasket,
      renameBasket,
      removeBasket,
      toggleTheme,
      reset,
    }),
    [
      state,
      addCard,
      removeCard,
      updateCard,
      setLayout,
      createBasket,
      duplicateBasket,
      importBasket,
      selectBasket,
      renameBasket,
      removeBasket,
      toggleTheme,
      reset,
    ],
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error("useWorkspace must be used within a WorkspaceProvider");
  }
  return ctx;
}
